import React, { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import BillingPeriodCell from '../BillingPeriodCell';
import {
  getActiveBillingCycles,
  setBillingCycleStatusPaid,
  setBillingCycleStatusRejected,
  undoBillingCycleMarkedSent,
} from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { PermissionSlug } from '../../../constants/featureFlags';
import { useStore } from '../../../store';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { BillingDataTable } from '../BillingDataTable';
import {
  hasInvoiceRow,
  manualMoveRefusal,
  showManualMoveFailures,
} from '../manualMoves';
import { BillingSelectionActionBar } from '../BillingSelectionActionBar';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn, writtenOffColumn } from '../billingTableUtils';
import { formatMoneyDisplay } from '../../../utils/currencyUtils';
import { expectedRevenueColumn } from '../expectedRevenueColumn';
import { PatientCell } from '../PatientCell';
import { InactiveEpisodeBadge } from '../InactiveEpisodeBadge';
import { InsuranceCell } from '../InsuranceCell';
import { NoRemittancesNotice } from '../NoRemittances';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';
import { WriteOffAction } from '../WriteOffAction';
import {
  VoidClaimsConfirmDialog,
  type VoidCopyOptions,
} from '../VoidClaimsButton';

interface EOEPendingPaymentTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEPendingPaymentTab: React.FC<EOEPendingPaymentTabProps> = ({
  onCountsInvalidated,
}) => {
  const [pushIdsOpen, setPushIdsOpen] = useState(false);
  const [paidConfirmOpen, setPaidConfirmOpen] = useState(false);
  const [rejectedConfirmOpen, setRejectedConfirmOpen] = useState(false);
  const [undoSentConfirmOpen, setUndoSentConfirmOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);

  const canSubmitClaims = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_SUBMIT)
  );

  const query = useBillingTableQuery('pending-payment', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const {
    data,
    isLoading,
    isRefreshing,
    isSubmitting,
    selectedIds,
    selectedItems,
    total,
    totalPages,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    showError,
    voidSelected,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    tabId: 'pending-payment',
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PENDING_PAYMENT'], ...query.toParams() },
    errorMessage: 'Could not fetch data for the "Pending Payment" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
    getBillType: item => item.bill_type,
  });

  const handleConfirmPaid = () => {
    setPaidConfirmOpen(false);
    void mutateSelected(
      id =>
        setBillingCycleStatusPaid({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles could not be marked as paid',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    );
  };

  const handleConfirmUndoSent = () => {
    setUndoSentConfirmOpen(false);
    void mutateSelected(
      id => undoBillingCycleMarkedSent({ path: { billing_cycle_id: id } }),
      'Some billing cycles could not be moved back to Ready to Send',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures =>
        showManualMoveFailures(
          failures,
          showError,
          'Some billing cycles could not be moved back to Ready to Send'
        )
    );
  };

  const handleVoid = (options: VoidCopyOptions) => {
    void voidSelected(options, onCountsInvalidated);
  };

  const handleConfirmRejected = () => {
    setRejectedConfirmOpen(false);
    void mutateSelected(
      id =>
        setBillingCycleStatusRejected({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles could not be marked as rejected',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    );
  };

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='flex flex-col items-start gap-1'>
            <PatientCell
              name={row.original.patient_name}
              mrn={row.original.patient_mrn}
              patientId={row.original.patient_id}
            />
            {row.original.episode_status === 'cancelled' && (
              <InactiveEpisodeBadge className='self-start' />
            )}
          </div>
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
            receivesRemittances={row.original.receives_remittances}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        cell: ({ getValue }) => formatMoneyDisplay(getValue<string | null>()),
        sortingFn: currencySortingFn,
      },
      expectedRevenueColumn,
      writtenOffColumn,
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.billing_period_end) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.billing_period_end}
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            billType={row.original.bill_type}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={
              <>
                <ClaimWorksheetViewerAction billingCycle={row.original} />
                <WriteOffAction
                  billingCycleId={row.original.billing_cycle_id}
                  claim={row.original}
                />
              </>
            }
          />
        ),
      },
    ],
    []
  );

  return (
    <div className='flex flex-col gap-4'>
      <NoRemittancesNotice rows={data} />
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        selection={{
          selectedIds,
          onToggleId: handleToggleId,
          onToggleAllVisible: handleToggleAll,
        }}
      />
      <Dialog open={pushIdsOpen} onOpenChange={setPushIdsOpen}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Selected billing cycle IDs</DialogTitle>
          </DialogHeader>
          <pre className='text-sm bg-muted rounded p-4 overflow-auto max-h-64 whitespace-pre-wrap break-all'>
            {Array.from(selectedIds).join('\n')}
          </pre>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={paidConfirmOpen}
        title='Mark claims as paid?'
        message="Marks the selected claims as Paid without recording a payment, so AR reports keep their full charges open. If you received payment outside the clearinghouse, record it as a new remittance in the Remittances view instead. An invoice-billed claim marked paid by hand can be undone with Undo Mark as Paid on the Paid tab; any other claim can't be undone."
        confirmLabel='Mark as Paid'
        onAccept={handleConfirmPaid}
        onCancel={() => setPaidConfirmOpen(false)}
      />
      <ConfirmationDialog
        open={rejectedConfirmOpen}
        title='Mark claims as rejected?'
        message="Marks the selected claims as Rejected. The billing cycle can't be resubmitted, and this can't be undone."
        confirmLabel='Mark as Rejected'
        onAccept={handleConfirmRejected}
        onCancel={() => setRejectedConfirmOpen(false)}
      />
      <ConfirmationDialog
        open={undoSentConfirmOpen}
        title='Undo Mark as Sent?'
        message='Moves the selected invoice claims back to Ready to Send and clears their Billed date.'
        confirmLabel='Undo Mark as Sent'
        onAccept={handleConfirmUndoSent}
        onCancel={() => setUndoSentConfirmOpen(false)}
      />
      <VoidClaimsConfirmDialog
        open={voidConfirmOpen}
        selectedCount={selectedIds.size}
        postSend={true}
        onConfirm={options => {
          setVoidConfirmOpen(false);
          handleVoid(options);
        }}
        onCancel={() => setVoidConfirmOpen(false)}
      />
      <BillingSelectionActionBar
        selectedItems={selectedItems}
        onClear={clearSelection}
        onRemoveSelectedId={removeSelectedId}
        actions={[
          ...(canSubmitClaims
            ? [
                {
                  key: 'mark-paid',
                  label: 'Mark as Paid',
                  disabledReason: () =>
                    isSubmitting || isLoading
                      ? 'Action in progress'
                      : undefined,
                  run: () => setPaidConfirmOpen(true),
                },
                {
                  key: 'mark-rejected',
                  label: 'Mark as Rejected',
                  destructive: true,
                  disabledReason: () =>
                    isSubmitting || isLoading
                      ? 'Action in progress'
                      : undefined,
                  run: () => setRejectedConfirmOpen(true),
                },
                {
                  key: 'undo-sent',
                  label: 'Undo Mark as Sent',
                  isVisible: hasInvoiceRow,
                  disabledReason: () =>
                    isSubmitting || isLoading
                      ? 'Action in progress'
                      : manualMoveRefusal('Undo Mark as Sent', selectedItems),
                  run: () => setUndoSentConfirmOpen(true),
                },
              ]
            : []),
          {
            key: 'void',
            label: 'Void',
            destructive: true,
            disabledReason: () =>
              isSubmitting || isLoading ? 'Action in progress' : undefined,
            run: () => setVoidConfirmOpen(true),
          },
        ]}
      />
    </div>
  );
};

export default EOEPendingPaymentTab;
